"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import {
  AlertTriangle,
  Check,
  KeyRound,
  Loader2,
  Mail,
  ShieldCheck,
  User,
  UserCircle2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { randomCaptchaPair } from "@/lib/signupCaptcha";
import { USERNAME_MAX_LEN, USERNAME_MIN_LEN } from "@/lib/usernameRules";
import UserAuthPage from "@/components/UserPanel/AuthPage";

export default function AgentSignup() {
  const { t } = useTranslation("panel");
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const captcha = useMemo(() => randomCaptchaPair(), []);
  const usernameCheckSeq = useRef(0);
  const [usernameStatus, setUsernameStatus] = useState("idle");
  const [recoveryStep, setRecoveryStep] = useState(null);
  const [recoverySavedConfirmed, setRecoverySavedConfirmed] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    username: "",
    email: "",
    referralId: "",
    password: "",
    confirmPassword: "",
    captchaAnswer: "",
    captchaChecked: false,
  });

  useEffect(() => {
    if (!router.isReady) return;
    const ref =
      typeof router.query.ref === "string" ? router.query.ref.trim() : "";
    const sponsor =
      typeof router.query.sponsor === "string"
        ? router.query.sponsor.trim()
        : "";
    const code = sponsor || ref;
    if (!code) return;
    setForm((s) => (s.referralId ? s : { ...s, referralId: code }));
  }, [router.isReady, router.query.ref, router.query.sponsor]);

  useEffect(() => {
    const raw = form.username.trim();
    const lower = raw.toLowerCase().replace(/-/g, "");

    if (!raw) {
      setUsernameStatus("idle");
      return;
    }
    if (!/^[A-Za-z0-9-]+$/.test(raw)) {
      setUsernameStatus("invalid");
      return;
    }
    if (lower.length < USERNAME_MIN_LEN) {
      setUsernameStatus("invalid");
      return;
    }

    const id = ++usernameCheckSeq.current;
    const ac = new AbortController();
    const debounceMs = 450;

    const tid = setTimeout(async () => {
      if (usernameCheckSeq.current !== id) return;
      setUsernameStatus("checking");
      try {
        const res = await fetch(
          `/api/agent/check-username?username=${encodeURIComponent(lower)}`,
          { signal: ac.signal },
        );
        const data = await res.json().catch(() => ({}));
        if (usernameCheckSeq.current !== id) return;
        if (!res.ok) {
          setUsernameStatus("idle");
          return;
        }
        if (data.invalid) {
          setUsernameStatus("invalid");
          return;
        }
        setUsernameStatus(data.available ? "available" : "taken");
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (usernameCheckSeq.current !== id) return;
        setUsernameStatus("idle");
      }
    }, debounceMs);

    return () => {
      clearTimeout(tid);
      ac.abort();
    };
  }, [form.username]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim()) return toast.error(t("agent.signup.error.nameRequired"));
    const uTrim = form.username.trim();
    const usernameOk = /^[A-Za-z0-9-]+$/.test(uTrim);
    if (!usernameOk) return toast.error(t("agent.signup.error.usernameInvalid"));
    const uNorm = uTrim.toLowerCase().replace(/-/g, "");
    if (uNorm.length < USERNAME_MIN_LEN) {
      return toast.error(t("agent.signup.error.usernameMin", { min: USERNAME_MIN_LEN }));
    }
    if (uNorm.length > USERNAME_MAX_LEN) {
      return toast.error(t("agent.signup.error.usernameMax", { max: USERNAME_MAX_LEN }));
    }
    if (usernameStatus === "taken") return toast.error(t("agent.signup.error.usernameTaken"));
    if (usernameStatus === "checking") return toast.error(t("agent.signup.error.usernameCheckWait"));
    const referralTrim = form.referralId.trim();
    if (!referralTrim) return toast.error(t("agent.signup.error.referralRequired"));
    if (!/^[A-Za-z0-9-]+$/.test(referralTrim)) {
      return toast.error(t("agent.signup.error.referralInvalid"));
    }
    if (!form.email) return toast.error(t("agent.signup.error.emailRequired"));
    if (!form.password) return toast.error(t("agent.signup.error.passwordRequired"));
    if (!form.confirmPassword) return toast.error(t("agent.signup.error.confirmRequired"));
    if (form.password !== form.confirmPassword) return toast.error(t("agent.signup.error.passwordsMismatch"));
    if (form.password.length < 8) return toast.error(t("agent.signup.error.passwordMin"));
    const captchaAns = parseInt(form.captchaAnswer.trim(), 10);
    const expected = captcha.a + captcha.b;
    if (
      !form.captchaChecked ||
      !Number.isInteger(captchaAns) ||
      captchaAns !== expected
    ) {
      return toast.error(t("agent.signup.error.captchaWrong"));
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/agent/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.fullName.trim(),
          username: uTrim,
          email: form.email.trim(),
          referralId: referralTrim,
          sponsorAgentId: referralTrim,
          password: form.password,
          confirmPassword: form.confirmPassword,
          captchaChecked: form.captchaChecked,
          captchaA: captcha.a,
          captchaB: captcha.b,
          captchaAnswer: form.captchaAnswer.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(
          typeof data.error === "string" ? data.error : t("agent.signup.error.registrationFailed"),
        );
        return;
      }
      if (
        typeof data.recoveryPhrase === "string" &&
        data.recoveryPhrase.trim()
      ) {
        setRecoverySavedConfirmed(false);
        setRecoveryStep(data.recoveryPhrase.trim());
        toast.success(t("agent.signup.toastSaveRecoveryFirst"));
        return;
      }
      toast.success(t("agent.signup.toastCreated"));
      router.push("/agent");
    } finally {
      setSubmitting(false);
    }
  };

  const recoveryWords = recoveryStep ? recoveryStep.split(/\s+/) : [];

  return (
    <UserAuthPage
      title={t("agent.signup.title")}
      subtitle={t("agent.signup.subtitle")}
      secondaryText={t("agent.signup.secondaryText")}
      secondaryHref="/agent"
      secondaryLabel={t("agent.signup.secondaryLabel")}
    >
      {recoveryStep ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="agent-recovery-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-border bg-bg-card p-5 shadow-xl"
          >
            <h2
              id="agent-recovery-title"
              className="font-display text-lg font-semibold text-text-primary"
            >
              {t("agent.signup.recovery.title")}
            </h2>
            <p className="mt-2 text-sm text-text-muted">{t("agent.signup.recovery.body")}</p>
            <div className="mt-3 rounded-lg border border-red-500/25 bg-red-500/10 p-3">
              <div className="flex gap-2">
                <AlertTriangle
                  size={16}
                  className="mt-0.5 shrink-0 text-accent-red"
                />
                <p className="text-xs text-text-primary">{t("agent.signup.recovery.warn")}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {recoveryWords.map((word, i) => (
                <div
                  key={`${i}-${word}`}
                  className="flex items-center gap-2 rounded-lg border border-border bg-bg-secondary px-2 py-2"
                >
                  <span className="w-5 shrink-0 text-xs font-semibold text-accent-amber">
                    {i + 1}
                  </span>
                  <span className="truncate text-sm text-text-primary">
                    {word}
                  </span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(recoveryStep);
                toast.success(t("agent.signup.recovery.copiedToast"));
              }}
              className="mt-4 w-full rounded-lg border border-border bg-bg-secondary py-2.5 text-sm font-medium text-text-primary hover:bg-white/5"
            >
              {t("agent.signup.recovery.copy")}
            </button>
            <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={recoverySavedConfirmed}
                onChange={(e) => setRecoverySavedConfirmed(e.target.checked)}
                className="mt-1"
              />
              <span>{t("agent.signup.recovery.checkbox")}</span>
            </label>
            <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              {t("agent.signup.pendingNotice")}
            </p>
            <button
              type="button"
              disabled={!recoverySavedConfirmed}
              onClick={() => {
                setRecoveryStep(null);
                toast.success(t("agent.signup.toastCreated"));
                router.push("/agent");
              }}
              className="mt-4 w-full rounded-lg bg-linear-to-br from-accent-amber to-accent-orange py-2.5 font-display text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("agent.signup.recovery.continue")}
            </button>
          </div>
        </div>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t("agent.signup.form.labelFullName")}
          </label>
          <div className="relative">
            <UserCircle2
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              value={form.fullName}
              onChange={(e) =>
                setForm((s) => ({ ...s, fullName: e.target.value }))
              }
              placeholder={t("agent.signup.form.placeholderFullName")}
              autoComplete="name"
              className="w-full rounded-lg border border-border bg-bg-secondary py-2.5 pl-10 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t("agent.signup.form.labelUsername")}
          </label>
          <div className="relative">
            <User
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              value={form.username}
              minLength={USERNAME_MIN_LEN}
              maxLength={USERNAME_MAX_LEN + 8}
              onChange={(e) =>
                setForm((s) => ({ ...s, username: e.target.value }))
              }
              placeholder={t("agent.signup.form.placeholderUsername")}
              autoComplete="username"
              className={`w-full rounded-lg border bg-bg-secondary py-2.5 pl-10 pr-10 text-sm text-text-primary placeholder:text-text-muted focus:outline-none ${
                usernameStatus === "available"
                  ? "border-green-500/50 focus:border-green-500"
                  : usernameStatus === "taken" || usernameStatus === "invalid"
                    ? "border-red-500/50 focus:border-red-500"
                    : "border-border focus:border-accent-amber"
              }`}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-text-muted">
              {usernameStatus === "checking" ? (
                <Loader2
                  className="h-4 w-4 animate-spin text-accent-amber"
                  aria-hidden
                />
              ) : usernameStatus === "available" ? (
                <Check className="h-4 w-4 text-green-500" aria-hidden />
              ) : usernameStatus === "taken" || usernameStatus === "invalid" ? (
                <X className="h-4 w-4 text-red-400" aria-hidden />
              ) : null}
            </span>
          </div>
          <p className="mt-1 text-xs text-text-muted">
            {t("agent.signup.form.usernameHint", { min: USERNAME_MIN_LEN, max: USERNAME_MAX_LEN })}
          </p>
          {usernameStatus === "available" ? (
            <p className="mt-1 text-xs text-green-500">{t("agent.signup.username.available")}</p>
          ) : null}
          {usernameStatus === "taken" ? (
            <p className="mt-1 text-xs text-red-400">{t("agent.signup.username.taken")}</p>
          ) : null}
          {usernameStatus === "invalid" && form.username.trim() ? (
            <p className="mt-1 text-xs text-red-400">
              {!/^[A-Za-z0-9-]+$/.test(form.username.trim())
                ? t("agent.signup.username.invalidChars")
                : form.username.trim().toLowerCase().replace(/-/g, "").length < USERNAME_MIN_LEN
                  ? t("agent.signup.username.invalidMin", { min: USERNAME_MIN_LEN })
                  : t("agent.signup.username.invalidGeneric")}
            </p>
          ) : null}
        </div>

        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t("agent.signup.form.labelReferral")}
          </label>
          <div className="relative">
            <KeyRound
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              value={form.referralId}
              onChange={(e) =>
                setForm((s) => ({ ...s, referralId: e.target.value }))
              }
              placeholder={t("agent.signup.form.placeholderReferral")}
              autoComplete="off"
              required
              className="w-full rounded-lg border border-border bg-bg-secondary py-2.5 pl-10 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
            />
          </div>
          <p className="mt-1 text-xs text-text-muted">
            {t("agent.signup.form.referralHint")}
          </p>
        </div>

        <div>
          <label className="mb-1.5 block text-sm text-text-muted">{t("agent.signup.form.labelEmail")}</label>
          <div className="relative">
            <Mail
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm((s) => ({ ...s, email: e.target.value }))
              }
              placeholder={t("agent.signup.form.placeholderEmail")}
              autoComplete="email"
              className="w-full rounded-lg border border-border bg-bg-secondary py-2.5 pl-10 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t("agent.signup.form.labelPassword")}
          </label>
          <input
            type="password"
            value={form.password}
            onChange={(e) =>
              setForm((s) => ({ ...s, password: e.target.value }))
            }
            placeholder={t("agent.signup.form.placeholderPassword")}
            autoComplete="new-password"
            className="w-full rounded-lg border border-border bg-bg-secondary px-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm text-text-muted">
            {t("agent.signup.form.labelConfirmPassword")}
          </label>
          <input
            type="password"
            value={form.confirmPassword}
            onChange={(e) =>
              setForm((s) => ({ ...s, confirmPassword: e.target.value }))
            }
            placeholder={t("agent.signup.form.placeholderConfirmPassword")}
            autoComplete="new-password"
            className="w-full rounded-lg border border-border bg-bg-secondary px-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
          />
        </div>

        <div className="space-y-2 rounded-lg border border-border bg-bg-secondary p-3">
          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={form.captchaChecked}
              onChange={(e) =>
                setForm((s) => ({ ...s, captchaChecked: e.target.checked }))
              }
            />
            <ShieldCheck size={16} className="text-accent-amber" />
            {t("agent.signup.form.captchaRobot")}
          </label>
          <label className="block text-xs text-text-muted">
            {t("agent.signup.form.captchaQuestion", { a: captcha.a, b: captcha.b })}
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={form.captchaAnswer}
            onChange={(e) =>
              setForm((s) => ({
                ...s,
                captchaAnswer: e.target.value.replace(/\D/g, ""),
              }))
            }
            placeholder={t("agent.signup.form.placeholderCaptcha")}
            className="w-full rounded-lg border border-border bg-bg-primary px-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-amber focus:outline-none"
          />
        </div>

        <button
          type="submit"
          disabled={
            submitting ||
            usernameStatus === "taken" ||
            usernameStatus === "invalid" ||
            (form.username.trim() !== "" &&
              /^[A-Za-z0-9-]+$/.test(form.username.trim()) &&
              form.username.trim().toLowerCase().replace(/-/g, "").length >=
                USERNAME_MIN_LEN &&
              usernameStatus === "checking")
          }
          className="w-full rounded-lg bg-linear-to-br from-accent-amber to-accent-orange py-2.5 font-display text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? t("agent.signup.form.submitting") : t("agent.signup.form.submit")}
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-text-muted">
        <Link href="/" className="text-accent-amber">
          {t("agent.signup.backToSite")}
        </Link>
      </p>
    </UserAuthPage>
  );
}
